import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/hooks/useAuth"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  listUsers,
  getUsageLimits,
  setRoleCredits,
  setUserCredits,
  setMonthlyTopup,
  type User,
  type UsageLimits as UsageLimitsData,
  type RoleName,
} from "@/services/adminService"

const ROLES: RoleName[] = ["owner", "admin", "member", "viewer"]

/**
 * Owner 専用: 利用上限の編集ページ。env 注入の既定の上に、テナント単位の上書き
 * （ロール別・ユーザー別の日次クレジット上限・テナント月次 topup）を即時反映で編集する。
 * admin には出ない（サイドバー minRole=owner ＋ ルートの RoleGuard requiredRole=owner、
 * API も owner 強制）。
 */
export default function UsageLimits() {
  const { token } = useAuth()
  const [data, setData] = useState<UsageLimitsData | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 入力欄のローカル値（role / user / monthly）。空文字＝未入力。
  const [roleInputs, setRoleInputs] = useState<Record<string, string>>({})
  const [userInputs, setUserInputs] = useState<Record<string, string>>({})
  const [topupInput, setTopupInput] = useState<string>("")

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const [limits, userList] = await Promise.all([
        getUsageLimits(token),
        listUsers(token),
      ])
      setData(limits)
      setUsers(userList)
      setTopupInput(String(limits.monthly.topup))
    } catch (e) {
      setError(e instanceof Error ? e.message : "読み込みに失敗しました")
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  // 上書きを userId -> credits に引きやすくする。
  const userOverrideMap: Record<string, number> = {}
  for (const o of data?.userOverrides ?? []) userOverrideMap[o.userId] = o.credits

  const handleRoleSave = async (role: RoleName, clear: boolean) => {
    if (!token) return
    const key = `role:${role}`
    setSavingKey(key)
    setError(null)
    try {
      let credits: number | null = null
      if (!clear) {
        const raw = roleInputs[role]
        const n = Number(raw)
        if (raw === "" || !Number.isInteger(n) || n < 0) {
          setError("クレジットは 0 以上の整数で入力してください")
          setSavingKey(null)
          return
        }
        credits = n
      }
      await setRoleCredits(token, role, credits)
      setRoleInputs((p) => ({ ...p, [role]: "" }))
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "保存に失敗しました")
    } finally {
      setSavingKey(null)
    }
  }

  const handleUserSave = async (userId: string, clear: boolean) => {
    if (!token) return
    const key = `user:${userId}`
    setSavingKey(key)
    setError(null)
    try {
      let credits: number | null = null
      if (!clear) {
        const raw = userInputs[userId]
        const n = Number(raw)
        if (raw === "" || !Number.isInteger(n) || n < 0) {
          setError("クレジットは 0 以上の整数で入力してください")
          setSavingKey(null)
          return
        }
        credits = n
      }
      await setUserCredits(token, userId, credits)
      setUserInputs((p) => ({ ...p, [userId]: "" }))
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "保存に失敗しました")
    } finally {
      setSavingKey(null)
    }
  }

  const handleTopupSave = async () => {
    if (!token) return
    setSavingKey("monthly")
    setError(null)
    try {
      const n = Number(topupInput)
      if (topupInput === "" || !Number.isInteger(n) || n < 0) {
        setError("topup は 0 以上の整数で入力してください")
        setSavingKey(null)
        return
      }
      await setMonthlyTopup(token, n)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "保存に失敗しました")
    } finally {
      setSavingKey(null)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">利用上限</h1>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-40 w-full" />
        ))}
      </div>
    )
  }

  const monthlyBase = data?.monthly.base ?? null
  const monthlyTopup = data?.monthly.topup ?? 0
  const monthlyEffective = data?.monthly.effective ?? null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">利用上限</h1>
        <p className="text-sm text-muted-foreground">
          クレジットの日次・月次の上限をこのテナントだけ調整します（即時反映・数十秒以内）。
          既定（環境設定）はそのまま、上書きが優先されます。オーナー専用です。
        </p>
      </div>

      {error && (
        <div className="rounded border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* 1. ロール別 日次クレジット上限 */}
      <Card>
        <CardHeader>
          <CardTitle>ロール別 日次クレジット上限</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">ロール</th>
                  <th className="text-left py-2 px-3">既定</th>
                  <th className="text-left py-2 px-3">現在の有効値</th>
                  <th className="text-left py-2 px-3">上書き</th>
                </tr>
              </thead>
              <tbody>
                {ROLES.map((role) => {
                  const def = data?.defaults[role] ?? 0
                  const override = data?.roleOverrides[role]
                  const effective = override ?? def
                  const key = `role:${role}`
                  return (
                    <tr key={role} className="border-b">
                      <td className="py-2 px-3 font-medium">{role}</td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {def.toLocaleString()}
                      </td>
                      <td className="py-2 px-3">
                        {effective.toLocaleString()}
                        {override !== undefined && (
                          <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">
                            上書き中
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            className="w-28"
                            placeholder={String(def)}
                            value={roleInputs[role] ?? ""}
                            onChange={(e) =>
                              setRoleInputs((p) => ({ ...p, [role]: e.target.value }))
                            }
                          />
                          <Button
                            size="sm"
                            disabled={savingKey === key}
                            onClick={() => handleRoleSave(role, false)}
                          >
                            設定
                          </Button>
                          {override !== undefined && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={savingKey === key}
                              onClick={() => handleRoleSave(role, true)}
                            >
                              既定に戻す
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 2. ユーザー別 日次クレジット上限 */}
      <Card>
        <CardHeader>
          <CardTitle>ユーザー別 日次クレジット上限</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">ユーザー</th>
                  <th className="text-left py-2 px-3">ロール</th>
                  <th className="text-left py-2 px-3">既定（ロール）</th>
                  <th className="text-left py-2 px-3">上書き</th>
                  <th className="text-left py-2 px-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const roleDefault =
                    data?.roleOverrides[user.role as RoleName] ??
                    data?.defaults[user.role as RoleName] ??
                    0
                  const override = userOverrideMap[user.userId]
                  const key = `user:${user.userId}`
                  return (
                    <tr key={user.userId} className="border-b">
                      <td className="py-2 px-3">
                        <div>{user.email}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {user.userId.slice(0, 12)}...
                        </div>
                      </td>
                      <td className="py-2 px-3">{user.role}</td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {roleDefault.toLocaleString()}
                      </td>
                      <td className="py-2 px-3">
                        {override !== undefined ? (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">
                            {override.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            className="w-28"
                            placeholder={String(roleDefault)}
                            value={userInputs[user.userId] ?? ""}
                            onChange={(e) =>
                              setUserInputs((p) => ({
                                ...p,
                                [user.userId]: e.target.value,
                              }))
                            }
                          />
                          <Button
                            size="sm"
                            disabled={savingKey === key}
                            onClick={() => handleUserSave(user.userId, false)}
                          >
                            設定
                          </Button>
                          {override !== undefined && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={savingKey === key}
                              onClick={() => handleUserSave(user.userId, true)}
                            >
                              クリア
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 3. テナント月次 topup */}
      <Card>
        <CardHeader>
          <CardTitle>テナント月次クレジット（topup）</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {monthlyBase === null ? (
            <p className="text-sm text-muted-foreground">
              月次上限（環境設定）は未設定のため無制限です。この状態では topup を設定しても
              合計は無制限のままになります（上限を設定するとそこに積み増しされます）。
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              env 既定 {monthlyBase.toLocaleString()} ＋ topup ={" "}
              <span className="font-medium text-foreground">
                合計 {monthlyEffective?.toLocaleString() ?? "—"}
              </span>{" "}
              クレジット / 月
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm">topup（積み増し・絶対値）:</span>
            <Input
              type="number"
              min={0}
              className="w-36"
              value={topupInput}
              onChange={(e) => setTopupInput(e.target.value)}
            />
            <Button
              size="sm"
              disabled={savingKey === "monthly"}
              onClick={handleTopupSave}
            >
              保存
            </Button>
            <span className="text-xs text-muted-foreground">
              現在の topup: {monthlyTopup.toLocaleString()}
            </span>
          </div>
        </CardContent>
      </Card>

      {data?.updatedAt && (
        <p className="text-xs text-muted-foreground">
          最終更新: {new Date(data.updatedAt).toLocaleString("ja-JP")}
        </p>
      )}
    </div>
  )
}
